import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  Clock,
  Filter,
  Search
} from 'lucide-react';
import { timesheetAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const AdminTimesheets = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [roleFilter, setRoleFilter] = useState('ALL'); // Add role filter
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheetIdSearch, setTimesheetIdSearch] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Update status filter when URL query param changes
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl && statusFromUrl !== statusFilter) {
      setStatusFilter(statusFromUrl);
    } else if (!statusFromUrl && statusFilter) {
      // Clear status filter when URL has no status parameter
      setStatusFilter('');
    }
  }, [searchParams]);

  // Update URL when status filter changes locally (but avoid if it matches URL param)
  useEffect(() => {
    const currentStatusInUrl = searchParams.get('status');
    if (statusFilter && currentStatusInUrl !== statusFilter) {
      setSearchParams({ status: statusFilter });
    } else if (!statusFilter && currentStatusInUrl) {
      setSearchParams({});
    }
  }, [statusFilter]);

  const { data: timesheets, isLoading } = useQuery(
    ['admin-timesheets', statusFilter],
    () => {
      // Use getAllTimesheets for all timesheets (when no filter or when filter is not PENDING)
      // Use getPendingTimesheets only when explicitly filtering for PENDING
      if (statusFilter === 'PENDING') {
        return timesheetAPI.getPendingTimesheets({ status: statusFilter });
      } else {
        // For all other cases (no filter, SUBMITTED, APPROVED, etc.), use getAllTimesheets
        return timesheetAPI.getAllTimesheets({ status: statusFilter || '' });
      }
    },
    {
      select: (response) => response.data.timesheets || response.data?.timesheets || [],
    }
  );

  const approveMutation = useMutation(
    (timesheetId) => timesheetAPI.approveTimesheet(timesheetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-timesheets');
        queryClient.invalidateQueries('admin-pending-timesheets');
        toast.success('Timesheet approved successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    ({ timesheetId, rejectionReason }) => timesheetAPI.rejectTimesheet(timesheetId, rejectionReason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-timesheets');
        queryClient.invalidateQueries('admin-pending-timesheets');
        toast.success('Timesheet rejected successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTimesheets = timesheets?.filter(timesheet => {
    // Role filter
    if (roleFilter !== 'ALL') {
      const employeeRole = timesheet.employee?.role;
      if (roleFilter === 'EMPLOYEE' && employeeRole !== 'EMPLOYEE') {
        return false;
      }
      if (roleFilter === 'MANAGER' && employeeRole !== 'MANAGER') {
        return false;
      }
    }
    
    // Status filter - handle special cases
    let matchesStatus = true;
    if (statusFilter) {
      if (statusFilter === 'SUBMITTED') {
        // For "submitted", include SUBMITTED, PENDING, and REUPDATED
        matchesStatus = timesheet.status === 'SUBMITTED' || 
                       timesheet.status === 'PENDING' || 
                       timesheet.status === 'REUPDATED';
      } else {
        // Exact match for other statuses
        matchesStatus = timesheet.status === statusFilter;
      }
    }
    
    if (!matchesStatus) {
      return false;
    }
    
    // Check if searching by timesheet ID
    const hasTimesheetIdSearch = timesheetIdSearch && timesheetIdSearch.trim() !== '';
    
    // If searching by ID, check ID match first
    if (hasTimesheetIdSearch) {
      const searchId = String(timesheetIdSearch).trim();
      const timesheetId = timesheet.id ? String(timesheet.id) : '';
      if (!timesheetId.includes(searchId)) {
        return false; // ID doesn't match, exclude this timesheet
      }
      // ID matches, include this timesheet
      return true;
    } else {
      // Not searching by ID, check employee and manager search
      const hasSearch = searchTerm && searchTerm.trim() !== '';
      if (hasSearch) {
        const searchLower = searchTerm.toLowerCase();
        const matchesEmployee = 
          timesheet.employee?.firstName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.lastName?.toLowerCase().includes(searchLower) ||
          timesheet.employee?.email?.toLowerCase().includes(searchLower) ||
          timesheet.description?.toLowerCase().includes(searchLower);
        
        // Also search in manager names (assignedByManagerNames)
        const matchesManager = timesheet.assignedByManagerNames?.toLowerCase().includes(searchLower) || false;
        
        return matchesEmployee || matchesManager;
      }
      // No search criteria, show all (already filtered by status and role)
      return true;
    }
  });

  const handleApprove = (timesheetId) => {
    if (window.confirm('Are you sure you want to approve this timesheet?')) {
      approveMutation.mutate(timesheetId);
    }
  };

  const handleReject = (timesheetId) => {
    const rejectionReason = window.prompt('Please provide a reason for rejection:');
    if (rejectionReason && rejectionReason.trim()) {
      rejectMutation.mutate({ timesheetId, rejectionReason: rejectionReason.trim() });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Timesheet Approvals</h1>
          <p className="text-gray-600 mt-1">Review and approve employee timesheets</p>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by employee/manager name, email, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10 text-gray-900 placeholder:text-gray-400"
                />
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by timesheet ID..."
                  value={timesheetIdSearch}
                  onChange={(e) => setTimesheetIdSearch(e.target.value)}
                  className="form-input pl-10 text-gray-900 placeholder:text-gray-400"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input text-gray-900 bg-white"
              >
                <option value="">All Status</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="form-input text-gray-900 bg-white"
              >
                <option value="ALL">All Roles</option>
                <option value="EMPLOYEE">Employee</option>
                <option value="MANAGER">Manager</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Timesheets List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {filteredTimesheets?.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {/* Clickable rows indicator */}
            <div className="px-6 py-2 bg-blue-50 border-b border-blue-200">
              <p className="text-xs text-blue-600 flex items-center">
                <FileText className="w-3 h-3 mr-1" />
                Click on any timesheet row to view full details
              </p>
            </div>
            {filteredTimesheets.map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => {
                  console.log('AdminTimesheets - Navigating to timesheet:', timesheet.id);
                  navigate(`/admin/timesheets/${timesheet.id}`);
                }}
                className="p-6 hover:bg-gray-50 hover:border-l-4 hover:border-primary-500 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(timesheet.status)}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 
                          onClick={(e) => {
                            e.stopPropagation();
                            const userId = timesheet.employee?.id;
                            if (userId) {
                              navigate(`/admin/users/${userId}/timesheets`);
                            }
                          }}
                          className="text-sm font-medium text-blue-600 group-hover:text-blue-700 transition-colors flex items-center cursor-pointer hover:underline"
                        >
                          {timesheet.employee?.firstName || ''} {timesheet.employee?.lastName || ''}
                          <span className="ml-2 text-gray-400 group-hover:text-primary-500 transition-colors">
                            →
                          </span>
                        </h3>
                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded border border-gray-300">
                          ID: {timesheet.id}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {timesheet.employee?.email}
                      </p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {timesheet.totalHours} hours • Submitted {format(new Date(timesheet.submittedAt), 'MMM dd, yyyy')}
                      </p>
                      {timesheet.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {timesheet.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                      {timesheet.status}
                    </span>
                    
                    <div className="flex items-center space-x-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          navigate(`/admin/timesheets/${timesheet.id}`);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                      
                      {(timesheet.status === 'SUBMITTED' || timesheet.status === 'PENDING') && (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleApprove(timesheet.id);
                            }}
                            disabled={approveMutation.isLoading}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                            title="Approve"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleReject(timesheet.id);
                            }}
                            disabled={rejectMutation.isLoading}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Reject"
                          >
                            <XCircle className="w-4 h-4" />
                          </motion.button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {timesheet.rejectionReason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">
                      <strong>Rejection Reason:</strong> {timesheet.rejectionReason}
                    </p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter 
                ? 'Try adjusting your search or filter criteria.'
                : 'No timesheets are currently pending approval.'
              }
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminTimesheets;

